import * as moment from 'moment-timezone';
import 'moment/locale/fr-ca';
import { ServiceInfo, ServiceMetaInfo } from './shared-constants';
import { DurationInputArg2 } from 'moment';

export enum TimeUnit {
	Minute = 'Minute',
	Hour = 'Hour',
	Day = 'Day',
	Week = 'Week',
	Month = 'Month',
	Counter = 'Counter'
}

export enum ServiceType {
	Private = 'Privé',
	PrivateWithApprobation = 'Privé avec approbation',
	Public = 'Public',
	PublicWithApprobation = 'Public avec approbation'
}

export enum ServiceStartRule {
	NotDefined = 'Inconnu',
	FirstDayOfNextMonth = 'Premier jour du prochain mois',
	SubscriptionDay = "Jour de l'inscription"
}

export enum PartialMonthCalculation {
	NotDefined = 'Inconnu',
	Ratio = 'Ratio',
	MiddleMonth = 'Middle Month'
}

export enum ServiceBillingType {
	Monthly = 'Mensuel',
	PerUse = "À l'utilisation",
	AtBooking = 'Au moment de la réservation',
	Decremental = "À l'utilisation (décompte)",
	Maintenance = '',
	LumpSum = 'Montant forfaitaire',
	Episodic = 'Épisodique',
	DAS = 'DAS (Déduction à la source)'
}

export interface PaymentInfo {
	amount: number;
	contractNo: number;
	financialInstitution: string;
	id: number;
	invoicePayments: InvoicePaymentInfo[];
	noReference: string;
	paymentMethodDescription: string;
	processDate: string;
}

export interface InvoicePaymentInfo {
	invoiceAmount: number;
	invoiceDate: string;
	invoiceDescription: string;
	invoiceId: number;
	invoiceTax1: number;
	invoiceTax2: number;
	invoiceTotalAmount: number;
	paidAmount: number;
}

export class Service {
	get availableFrom() {
		return this._data.availableFrom;
	}

	get availableFromFormated() {
		return moment(this.availableFrom).tz('America/Montreal').format('LL');
	}

	get availableServiceId() {
		return this._data.availableServiceId;
	}

	get availableUntil() {
		return this._data.availableUntil;
	}

	get isExpired() {
		return moment(this.availableUntil).toDate().getTime() < moment().toDate().getTime();
	}

	get availableUntilFormated() {
		if (moment(this.availableUntil).get('year') === 9999) {
			return 'Indéterminée';
		}
		return moment(this.availableUntil).tz('America/Montreal').format('LL');
	}

	get cancelationNotice() {
		return this._data.cancelationNotice;
	}

	get cancelationNoticeTimeUnit() {
		return this._data.cancelationNoticeTimeUnit;
	}

	get cancelationRule() {
		if (!this.isCancelable) {
			return 'Impossible';
		}
		if (this.isCancelationFirstDayOfMonth) {
			return 'Premier jour du mois suivant';
		}
		return `${this._data.cancelationNotice} ${this._data.cancelationNoticeTimeUnit}`;
	}

	get duration() {
		return this._data.duration;
	}

	get durationTime() {
		return this.isFixedDuration ? `${this.duration} ${this.durationTimeUnit}` : 'Indéterminée';
	}

	get durationTimeUnit() {
		return this._data.durationTimeUnit;
	}
	get durationTimeUnitMoment() {
		if (this._data.durationTimeUnit === 'Day') {
			return 'days';
		}
		if (this._data.durationTimeUnit === 'Week') {
			return 'weeks';
		}
		if (this._data.durationTimeUnit === 'Month') {
			return 'months';
		}
		return 'days';
	}

	get formattedDurationTimeUnit() {
		let timeValue = '';
		if (this._data.durationTimeUnit === 'Week') {
			timeValue = 'semaine';
			if (this._data.duration > 1) {
				timeValue += 's';
			}
		} else if (this._data.durationTimeUnit === 'Day') {
			timeValue = 'jour';
			if (this._data.duration > 1) {
				timeValue += 's';
			}
		} else if (this._data.durationTimeUnit === 'Month') {
			timeValue = 'mois';
		}

		return timeValue;
	}

	get endDate() {
		if (this.period && (this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.period.endDateRaw))) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		const endDate = moment(this.onSaleUntil).tz('America/Montreal');

		return endDate;
	}

	get endDateFormatted() {
		if (this.period && (this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.period.endDateRaw))) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day').format('LL');
		}
		if (moment(this.endDate).get('year') === 9999) {
			return 'Indéterminée';
		}
		return this.isFixedDuration ? moment(this.endDate).tz('America/Montreal').format('LL') : 'Indéterminée';
	}

	get havePartialAmmount() {
		if (this.price === 0) {
			return false;
		}
		if (
			((this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') ||
				(this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic')) &&
			((this.rawData.serviceStartRule && this.rawData.serviceStartRule.toString() === 'FirstDayOfNextMonth') ||
				(this.rawData.serviceStartRule && this.rawData.serviceStartRule.toString() === 'FirstDayOfCurrentMonth'))
		) {
			return false;
		}
		return true;
	}

	get isApprovable() {
		return (
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PublicWithApprobation') ||
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PrivateWithApprobation')
		);
	}

	get isBankingCardPaymentAccepted() {
		return this._data.isBankingCardPaymentAccepted;
	}

	get isCancelable() {
		return this._data.isCancelable;
	}

	get isCancelationFirstDayOfMonth() {
		return this._data.isCancelationFirstDayOfMonth;
	}

	get isDAS() {
		return (
			(this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') ||
			(this.rawData.isDasPaymentAccepted &&
				!this.rawData.isPpaPaymentAccepted &&
				!this.rawData.isBankingCardPaymentAccepted &&
				!this.rawData.isFinancialInstitutionPaymentAccepted)
		);
	}

	get isDasPaymentAccepted() {
		return this._data.isDasPaymentAccepted;
	}

	get isEpisodic() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic';
	}

	get isFinancialInstitutionPaymentAccepted() {
		return this._data.isFinancialInstitutionPaymentAccepted;
	}

	get isFixedDuration() {
		return this._data.isFixedDuration;
	}

	get isMonthly() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly';
	}

	get isPerUse() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse';
	}
	get isAtBooking() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'AtBooking';
	}

	get isDecremental() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Decremental';
	}

	get isLumpSum() {
		return this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum';
	}

	get isPpaPaymentAccepted() {
		return this._data.isPpaPaymentAccepted;
	}

	get isPrivate() {
		return (
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'Private') ||
			(this.rawData.serviceType && this.rawData.serviceType.toString() === 'PrivateWithApprobation')
		);
	}

	get isSubscriptionAvailable() {
		return this._data.isSubscriptionAvailable;
	}

	get isTax1Exempted() {
		return this._data.isTax1Exempted;
	}

	get isTax2Exempted() {
		return this._data.isTax2Exempted;
	}

	get isTax3Exempted() {
		return this._data.isTax3Exempted;
	}

	get meta(): ServiceMetaInfo {
		return this._data.meta || { note: null };
	}

	get onSaleFrom() {
		return this._data.onSaleFrom;
	}

	get onSaleFromFormated() {
		return moment(this.onSaleFrom).tz('America/Montreal').format('LL');
	}

	get onSaleUntil() {
		return this._data.onSaleUntil;
	}

	get onSaleUntilFormated() {
		if (
			this.rawData.serviceBillingType &&
			this.rawData.serviceBillingType.toString() === 'LumpSum' &&
			this.durationTimeUnit === 'Week'
		) {
			if (this.duration) {
				const durationTimeUnit: DurationInputArg2 = this.rawData.durationTimeUnit as DurationInputArg2;
				const duration = Number(this.rawData.duration);
				const end = moment(this.onSaleFrom)
					.clone()
					// @ts-ignore -- moment type mismatch with DurationInputArg2
					.add(duration, durationTimeUnit)
					.endOf('day')
					.format('LL');

				return end;
			}
		}
		if (moment(this.onSaleUntil).get('year') === 9999) {
			return 'Indéterminée';
		}
		return moment(this.onSaleUntil).tz('America/Montreal').format('LL');
	}

	get parkIdentificationNo() {
		return this._data.parkIdentificationNo;
	}

	get partialEndDate() {
		if (this.period && this.period.force) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (
			this.period &&
			this._data.serviceBillingType.toString() === 'LumpSum' &&
			(this.durationTimeUnit === 'Week' || this.durationTimeUnit === 'Day')
		) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			if (!this.isFixedDuration) {
				return moment(this.onSaleUntil).tz('America/Montreal');
			} else {
				return this.startDate.add(1, 'month').startOf('month').subtract(1, 'day').endOf('day').tz('America/Montreal');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
			} else {
				return moment(this.rawData.onSaleUntil).tz('America/Montreal').endOf('day');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}

		return moment(this.onSaleUntil).tz('America/Montreal');
	}

	get partialEndDateFormatted() {
		return this.partialEndDate.format('LL');
	}

	get partialInvoiceToDate() {
		if (this.period && this.period.force) {
			return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			return this.startDate.clone().add(1, 'month').startOf('month').subtract(1, 'day').endOf('day').tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			return this.partialEndDate;
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			if (this.period && (this.durationTimeUnit === 'Week' || this.durationTimeUnit === 'Day')) {
				return moment(this.period.endDateRaw).tz('America/Montreal').endOf('day');
			} else {
				return moment(this.onSaleUntil).tz('America/Montreal');
			}
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'DAS') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		if (this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse') {
			return moment(this.onSaleUntil).tz('America/Montreal');
		}
		return this.startDate;
	}

	get partialInvoiceToDateFormatter() {
		return this.partialInvoiceToDate.format('LL');
	}

	get partialMonthCalculation() {
		return this._data.partialMonthCalculation;
	}

	get partialPrice() {
		const currentDay = this.startDate.date();
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'PerUse') {
			return this.rawData.price || 0;
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'LumpSum') {
			if (this.serviceStartRule === 'FirstDayOfFlatRateDuration') {
				return this.rawData.price || 0;
			}
			if (
				this.serviceStartRule === 'SubscriptionDay' &&
				this.period &&
				this.period.startDateRaw &&
				this.period.endDateRaw &&
				this.rawData.price > 0
			) {
				Math.ceil(
					moment(this.period.endDateRaw)
						.clone()
						.endOf('day')
						.diff(moment(this.period.startDateRaw).clone().startOf('day'), 'days', true)
				) - 1;
				return this.rawData.price;
			}
			moment(this.rawData.onSaleFrom).tz('America/Montreal');
			const availableUntil = moment(this.rawData.onSaleUntil).tz('America/Montreal');

			const countMonths = Math.ceil(availableUntil.endOf('month').diff(this.startDate.clone().startOf('month'), 'months', true));

			const duration = this.rawData.duration || 1;
			const amount = this.rawData.price || 0;

			const priceByMonth = amount / duration;

			this.startDate.clone().startOf('months');

			if (
				(this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'Ratio') ||
				(this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'MiddleMonth')
			) {
				if (this.period && this.durationTimeUnit === 'Week') {
					return parseFloat((this.price * (this.period.months || 1)).toString()).toFixed(2);
				}
				let cost = Number(priceByMonth);

				const daysInMonth = this.startDate.daysInMonth();
				daysInMonth - currentDay + 1;

				const total = countMonths * Number(priceByMonth);

				cost = total;
				return parseFloat(cost.toString()).toFixed(2);
			}
			if (this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'OldMiddleMonth') {
				let cost = 0;
				if (currentDay >= 15) {
					cost = Number((Number(priceByMonth) / 2).toFixed(2));
				} else {
					cost = Number(priceByMonth);
				}

				cost = Number(cost) + countMonths * Number(priceByMonth);

				return parseFloat(cost.toString()).toFixed(2);
			}

			return parseFloat(amount.toString()).toFixed(2);
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Monthly') {
			let billingDate = moment(this.startDate);
			let ApplyMiddleMonth = false;
			if (this.rawData.partialMonthCalculation && this.rawData.partialMonthCalculation.toString() === 'MiddleMonth') {
				if (currentDay >= 1 && currentDay <= 14) {
					billingDate = billingDate.clone().add('days', currentDay * -1 + 1);
				} else {
					ApplyMiddleMonth = true;
					billingDate = billingDate.clone().add('days', currentDay * -1 + 15);
				}
			}
			const daysInMonth = billingDate.daysInMonth() || 0;
			const daysToPay = daysInMonth - currentDay + 1 || 0;

			let amount = (daysToPay / daysInMonth) * (this.rawData.price || 0);

			if (ApplyMiddleMonth) {
				amount = (this.rawData.price || 0) / 2;
			}

			return parseFloat(amount.toString()).toFixed(2);
		}
		if (this.rawData && this.rawData.serviceBillingType && this.rawData.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return parseFloat((this.price * (this.period.months || 1)).toString()).toFixed(2);
			} else {
				const start = moment();
				if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
					start.endOf('month').add(1, 'day').startOf('day');
				}
				if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
					start.startOf('month');
				}

				const availableUntil = moment(this.rawData.onSaleUntil).tz('America/Montreal');

				const totalMonths = Math.ceil(availableUntil.diff(start, 'month', true));

				return parseFloat((this.price * totalMonths).toString()).toFixed(2);
			}
		}
		return 0;
	}

	get period() {
		return this._data.period;
	}

	get price(): number {
		return Number(parseFloat((this._data && this._data.price && this._data.price.toString()) || '0'));
	}

	get rawData() {
		return this._data || ({} as ServiceInfo);
	}

	get sectorId() {
		return this._data.sectorId;
	}

	get serviceBillingType() {
		if (this._data && this._data.serviceBillingType) {
			return ServiceBillingType[this._data && this._data.serviceBillingType];
		} else {
			return '';
		}
	}

	get serviceDescription() {
		return this._data.serviceDescription;
	}

	get serviceStartRule() {
		return this._data.serviceStartRule;
	}

	get serviceType() {
		return this._data.serviceType;
	}

	get startDate() {
		if (this.period && (this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.period.startDateRaw))) {
			return moment(this.period.startDateRaw).tz('America/Montreal');
		}
		const now = moment(new Date(this.availableFrom).getTime() > this.today.toDate().getTime() ? this.availableFrom : this.today);

		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'Episodic') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'Monthly') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceBillingType && this._data.serviceBillingType.toString() === 'DAS') {
			if (this.period) {
				return moment(this.period.startDateRaw).tz('America/Montreal');
			}
		}
		if (this._data && this._data.serviceStartRule && this._data.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			return now.clone().add(1, 'months').startOf('month').tz('America/Montreal');
		}
		if (this._data && this._data.serviceStartRule && this._data.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			return now.clone().startOf('month').tz('America/Montreal');
		}
		return now.tz('America/Montreal');
	}

	get startDateFormatted() {
		if (this.period && (this.period.force || (this._data.serviceBillingType.toString() === 'LumpSum' && this.period.startDateRaw))) {
			return moment(this.period.startDateRaw).tz('America/Montreal').format('LL');
		}
		return moment(this.startDate).tz('America/Montreal').format('LL');
	}

	today = moment().tz('America/Montreal').startOf('day');

	private _data: ServiceInfo = {
		availableServiceId: 0,
		cancelationNotice: 0,
		cancelationNoticeTimeUnit: TimeUnit.Month,
		duration: 0,
		durationTimeUnit: TimeUnit.Month,
		isBankingCardPaymentAccepted: false,
		isCancelable: false,
		isCancelationFirstDayOfMonth: false,
		isDasPaymentAccepted: false,
		isFinancialInstitutionPaymentAccepted: false,
		isFixedDuration: false,
		isPpaPaymentAccepted: false,
		isSubscriptionAvailable: false,
		isTax1Exempted: false,
		isTax2Exempted: false,
		isTax3Exempted: false,
		parkIdentificationNo: 0,
		partialMonthCalculation: 'NotDefined',
		price: 0,
		sectorId: 0,
		priceListingInfo: [],
		serviceBillingType: 'Monthly',
		serviceDescription: '',
		serviceStartRule: 'NotDefined',
		serviceType: 'Private',
		availableFrom: new Date().toISOString(),
		availableUntil: new Date().toISOString(),
		onSaleFrom: new Date().toISOString(),
		onSaleUntil: new Date().toISOString(),
		period: null,
		meta: null
	};

	constructor(data: ServiceInfo) {
		if (data) {
			Object.keys(data).forEach(k => {
				if (this._data[k] !== undefined) {
					this._data[k] = data[k];
				}
			});
		}
	}

	isSubscriptionDay() {
		if (this.serviceStartRule && this.serviceStartRule.toString() === 'SubscriptionDay') {
			return true;
		}
		return false;
	}

	getPricingPeriods(_includeBefore = false) {
		const filteredPriceListingInfo = this._data.priceListingInfo.filter(item => {
			const effectiveDate = moment(item.EffectiveDate);
			const currentDate = moment();
			const futureDate = moment().add(31, 'days');

			return effectiveDate.isSameOrBefore(futureDate) && effectiveDate.isSameOrAfter(currentDate);
		});

		filteredPriceListingInfo.forEach(item => {
			const effectiveDate = moment(item.EffectiveDate);
			const formattedDate = effectiveDate.format('DD MMMM YYYY');

			item.EffectiveDateFormated = `à partir du ${formattedDate}`;
			item.Price = parseFloat(item.Price.toString()).toFixed(2);
		});

		return filteredPriceListingInfo;
	}
	getAvailablePeriods(ss: any = null, isScope = true) {
		const periods: any[] = [];

		const endAt = moment(this.onSaleUntil).endOf('day');
		const today = moment(ss ? ss.startDateRaw : this.startDate) || moment();
		const onSaleFrom = moment(this.onSaleFrom).startOf('day');

		const start = moment(onSaleFrom.isAfter(today) ? onSaleFrom.toDate() : today.toDate());

		if (this.duration === 365 && this.isLumpSum) {
			const saleFrom = moment(this.onSaleFrom).startOf('day');
			const saleEnd = moment(this.onSaleUntil).endOf('day');

			return [
				{
					value: 0,
					start: saleFrom.format('LL'),
					end: saleEnd.format('LL'),
					months: 1,
					endDateRaw: saleEnd.toISOString() as string,
					startDateRaw: saleFrom.toISOString() as string
				}
			];
		}

		if (!ss && !isScope && !this.isDecremental) {
			const toDay = moment().startOf('day');
			const nextMonthStart = toDay.clone().endOf('month').add(1, 'day').startOf('month');
			const currentMonthEnd = toDay.clone().endOf('month').startOf('day');

			return [
				{
					value: 0,
					start: toDay.format('LL'),
					end: toDay.format('LL'),
					months: 1,
					endDateRaw: toDay.toISOString() as string,
					startDateRaw: toDay.toISOString() as string
				},
				{
					value: 1,
					start: nextMonthStart.format('LL'),
					end: moment.min(currentMonthEnd, endAt).format('LL'),
					months: 2,
					endDateRaw: moment.min(currentMonthEnd, endAt).toISOString() as string,
					startDateRaw: nextMonthStart.toISOString() as string
				}
			].filter(p => moment(p.startDateRaw).isSameOrBefore(endAt));
		}

		if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfNextMonth') {
			if (start.toDate().getTime() < new Date().getTime()) {
				start.endOf('month').add(1, 'day').startOf('day');
			}
		}

		if (this.serviceStartRule && this.serviceStartRule.toString() === 'FirstDayOfCurrentMonth') {
			start.startOf('month');
		}

		if (!ss && !isScope && start.format('M') === moment().format('M') && Number(start.format('D')) > 15) {
			start.add(1, 'month').startOf('month');
		}

		let months = Math.ceil(endAt.diff(start, 'month', true));

		if (!ss && months >= 2) {
			if (this.startDate.toDate().getTime() < new Date().getTime()) {
				months = 3;
			} else {
				months = 2;
			}
		}

		for (let i = 0; i < Math.min(months, 120); i++) {
			let rawEnd = start.clone().add(i, 'months').endOf('month');

			if (this.durationTimeUnit === 'Week' && this.duration > 0) {
				const duration = Number((i + 1) * Number(this.rawData.duration));
				rawEnd = start
					.clone()
					// @ts-ignore -- moment type mismatch with DurationInputArg2
					.add(duration, this.rawData.durationTimeUnit as DurationInputArg2)
					.subtract(1, 'day')
					.endOf('day');
			}

			if (
				this.durationTimeUnit === 'Day' &&
				this.duration > 0 &&
				this.isLumpSum &&
				this.serviceStartRule.toString() === 'SubscriptionDay'
			) {
				const duration = Number((i + 1) * Number(this.rawData.duration));
				rawEnd = start.clone().add(duration, 'days').subtract(1, 'day').endOf('day');
			}

			let s = start.clone().add(i, 'months').startOf('month');

			if (!i && this.serviceStartRule && this.serviceStartRule.toString() === 'SubscriptionDay') {
				if (this.startDate.toDate().getTime() < new Date().getTime()) {
					s = moment().startOf('day');
				}
			}

			const startRaw = (ss && start.toISOString()) || s.toISOString();
			const startMoment = moment(startRaw);

			if (startMoment.isAfter(endAt)) {
				break;
			}

			const end = moment.min(rawEnd, endAt);

			if (end.isBefore(startMoment)) {
				break;
			}

			periods.push({
				value: i,
				start: (ss && start.format('LL')) || s.format('LL'),
				end: end.format('LL'),
				months: end.diff(startMoment, 'months') + 1,
				endDateRaw: end.toISOString() as string,
				endDateRaw3_: end.toISOString() as string,
				startDateRaw: startRaw as string
			});

			if (end.isSame(endAt)) {
				break;
			}
		}

		if (this.duration === 365) {
			return periods.slice(0, 1);
		}

		return periods.slice(0, 16);
	}

	set(name, value) {
		const newData = { ...this._data };

		newData[name] = value;

		this._data = newData;
	}

	setToday(date) {
		this.today = moment(date).tz('America/Montreal').startOf('day');
	}
}
